/**
 * ProfileNotes — live profile artifact for extraction sessions.
 * Renders emerging profile as natural-language notes grouped by work history,
 * skills, projects, and identity. Inline approve/reject for pending claims.
 * Includes inline career timeline for temporal error detection.
 */

import { useState, useEffect, useRef } from 'preact/hooks';
import { CareerTimeline, careerTimelineStyles } from './CareerTimeline';

interface Claim {
	id: string;
	sessionId?: string;
	section: string;
	field: string;
	value: unknown;
	confidence: string;
	timestamp: number;
	agentReasoning?: string;
	narrative?: string;
	trustTier?: string;
}

// --- Compiled Profile View types (mirrors backend CompiledProfileView) ---

export interface ClaimRef {
	id: string;
	sessionId: string;
	section: string;
	field: string;
	value: unknown;
	confidence: string;
	narrative?: string;
	trustTier?: string;
	timestamp: number;
}

export interface CompiledProjectEntry {
	name: string;
	period: string;
	role: string;
	summary: string;
	problemStatement: string;
	status: string;
	launchDate: string;
	technicalDetails?: { builtWith?: string[]; platforms?: string[]; dataSources?: string[] };
	entityKey: string;
	claims: ClaimRef[];
	[key: string]: unknown; // Future fields — generic renderer catches these
}

export interface CompiledWorkEntry {
	company: string;
	title: string;
	period: string;
	location: string;
	focus: string;
	managementNote: string;
	trajectoryPhase: string;
	postDepartureContext: string;
	entityKey: string;
	betaEnrichment?: {
		verified: boolean;
		entityKey: string;
		entityType?: string;
		ticker?: string;
		sector?: string;
		scaleDescription?: string;
		hasPublicPage?: boolean;
		enrichedAt: string;
	};
	trustTier?: string;
	claims: ClaimRef[];
	projects: CompiledProjectEntry[];
	[key: string]: unknown; // Future fields
}

export interface CompiledPersonEntry {
	name: string;
	title: string;
	relationship: string;
	companies: string[];
	context: string;
	recommendations: { text: string; authorTitle?: string; date?: string; platform?: string }[];
	entityKey: string;
	claims: ClaimRef[];
	[key: string]: unknown; // Future fields
}

export interface CompiledEventEntry {
	name: string;
	type: string;
	date: string;
	company: string;
	significance: string;
	personalContext: string;
	entityKey: string;
	claims: ClaimRef[];
}

export interface CompiledProfileView {
	workHistory: CompiledWorkEntry[];
	people: CompiledPersonEntry[];
	events: CompiledEventEntry[];
	identity: { claims: ClaimRef[] };
	skills: { claims: ClaimRef[] };
	stats: { totalClaims: number; sessionCount: number };
}

interface Props {
	/** Pre-compiled profile view from backend — single source of truth for structure */
	compiledView: CompiledProfileView;
	/** Pending claims from current session (rare — claims are auto-approved) */
	pendingClaims?: Claim[];
	onClaimAction: (claimId: string, action: 'approve' | 'reject', editedValue?: unknown) => void;
	/** When set, claims in this set are highlighted as "from this session"; others are dimmed */
	sessionClaimIds?: Set<string>;
	/** When true, approved claims show edit/remove actions (for profile editor) */
	editable?: boolean;
	/** Claim IDs that just arrived (for highlight animation) */
	recentClaimIds?: Set<string>;
	/** Callback when user wants to reorganize a section via correction session */
	onReorganize?: (target: string, targetSection: string) => void;
}

interface ProjectGroup {
	name: string;
	period: string;
	role: string;
	summary: string;
	problemStatement: string;
	status: string;
	launchDate: string;
	technicalDetails?: { builtWith?: string[]; platforms?: string[]; dataSources?: string[] };
	claims: Claim[];
	extra: Record<string, unknown>; // Future fields not explicitly handled
}

interface WorkGroup {
	company: string;
	title: string;
	period: string;
	postDepartureContext: string;
	sectionIndex: number; // work_history[N] index for correction targeting
	betaEnrichment?: CompiledWorkEntry['betaEnrichment'];
	trustTier?: string;
	claims: Claim[]; // experience-level claims (not project-specific)
	projects: ProjectGroup[];
	status: 'pending' | 'approved' | 'mixed';
	extra: Record<string, unknown>; // Future fields not explicitly handled
}

interface RecommendationDisplay {
	text: string;
	platform?: string;
	date?: string;
	authorTitle?: string;
}

interface PersonGroup {
	name: string;
	title: string;
	relationship: string;
	companies: string;
	projectRefs: string;
	context: string;
	recommendations: RecommendationDisplay[];
	claims: Claim[];
}

interface EventGroup {
	name: string;
	type: string;
	date: string;
	significance: string;
	personalContext: string;
	projectRefs: string;
	company: string;
	claims: Claim[];
}

/** Extract string value from a claim */
function str(claim: Claim | undefined): string {
	if (!claim) return '';
	if (typeof claim.value === 'string') return claim.value;
	if (typeof claim.value === 'number') return String(claim.value);
	return '';
}

function formatItem(item: unknown): string {
	if (typeof item === 'string') return item;
	if (typeof item === 'object' && item !== null) {
		const obj = item as Record<string, unknown>;
		// Common patterns: {name, context}, {name, depth}, {url, label}
		if (obj.name) {
			const parts = [String(obj.name)];
			// Collect all non-name scalar properties as detail
			const detail = Object.entries(obj)
				.filter(([k, v]) => k !== 'name' && v !== null && v !== undefined && v !== '' && typeof v !== 'object')
				.map(([, v]) => String(v));
			if (detail.length > 0) parts.push(detail.join(', '));
			return parts.join(' — ');
		}
		if (obj.label) return String(obj.label);
		if (obj.url) return String(obj.url);
		return Object.entries(obj)
			.filter(([, v]) => v !== null && v !== undefined && v !== '')
			.filter(([, v]) => typeof v !== 'object')
			.map(([k, v]) => `${k.replace(/_/g, ' ')}: ${String(v)}`)
			.join(', ');
	}
	return String(item);
}

function formatValue(value: unknown): string {
	if (typeof value === 'string') return value;
	if (Array.isArray(value)) return value.map(formatItem).join(', ');
	if (typeof value === 'object' && value !== null) {
		return formatItem(value);
	}
	return String(value);
}

/** Extract a sortable year from a period value (higher = more recent) */
function extractSortYear(period: unknown): number {
	if (!period) return 0;
	const s = String(period);
	if (s.toLowerCase().includes('present')) return 9999;
	// Use START year (first mentioned) for chronological ordering
	const match = s.match(/\d{4}/);
	return match ? parseInt(match[0]) : 0;
}

// --- Field display registry ---
// Known fields get polished labels. Unknown future fields fall back to humanized key names.
// Render hints: 'paragraph' for long text, 'text' for short inline, 'badge' for status-like,
// 'tags' for arrays. Fields not in the registry render based on value type.
const FIELD_REGISTRY: Record<string, { label: string; render: 'text' | 'paragraph' | 'badge' | 'tags' }> = {
	summary: { label: 'Summary', render: 'paragraph' },
	problemStatement: { label: 'Problem', render: 'paragraph' },
	status: { label: 'Status', render: 'badge' },
	launchDate: { label: 'Launched', render: 'text' },
	postDepartureContext: { label: 'After departure', render: 'paragraph' },
};

// Fields explicitly handled in rendering — anything NOT in this set gets generic fallback
const KNOWN_PROJECT_FIELDS = new Set(['name', 'period', 'role', 'summary', 'problemStatement', 'status', 'launchDate', 'technicalDetails', 'entityKey', 'claims']);
const KNOWN_WORK_FIELDS = new Set(['company', 'title', 'period', 'location', 'focus', 'managementNote', 'trajectoryPhase', 'postDepartureContext', 'entityKey', 'betaEnrichment', 'trustTier', 'claims', 'projects']);

/** Convert camelCase key to readable label */
function humanizeKey(key: string): string {
	return key
		.replace(/([A-Z])/g, ' $1')
		.replace(/^./, c => c.toUpperCase())
		.trim();
}

/** Render a single field value generically based on its type and optional registry entry */
function renderGenericField(key: string, value: unknown) {
	if (value == null || value === '') return null;

	const reg = FIELD_REGISTRY[key];
	const label = reg?.label || humanizeKey(key);

	if (Array.isArray(value)) {
		if (value.length === 0) return null;
		return (
			<div class="pn-tag-row" key={key}>
				<span class="pn-tag-label">{label}</span>
				{value.map((item, i) => <span key={i} class="pn-tag">{String(item)}</span>)}
			</div>
		);
	}

	if (typeof value === 'object') {
		// Render each sub-field of the object
		const entries = Object.entries(value as Record<string, unknown>)
			.filter(([, v]) => v != null && v !== '' && !(Array.isArray(v) && v.length === 0));
		if (entries.length === 0) return null;
		return (
			<div class="pn-field-group-meta" key={key}>
				{entries.map(([k, v]) => renderGenericField(k, v))}
			</div>
		);
	}

	if (reg?.render === 'badge') {
		return <span class="pn-status-badge" key={key}>{String(value)}</span>;
	}

	if (reg?.render === 'paragraph' || String(value).length > 80) {
		return <p class="pn-field-text" key={key}><span class="pn-meta-label">{label}:</span> {String(value)}</p>;
	}

	return <span class="pn-field-inline" key={key}><span class="pn-meta-label">{label}:</span> {String(value)}</span>;
}

/** Collect unknown fields from a compiled entry for generic rendering */
function collectExtra(entry: Record<string, unknown>, knownFields: Set<string>): Record<string, unknown> {
	const extra: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(entry)) {
		if (!knownFields.has(key) && value != null && value !== '') {
			extra[key] = value;
		}
	}
	return extra;
}

/**
 * Build internal rendering structures from CompiledProfileView.
 * The backend compiler is the single source of truth for grouping — this just maps
 * the compiled shape to internal types for rendering.
 */
function buildFromCompiled(compiledView: CompiledProfileView, pendingClaims?: Claim[]) {
	// Coerce compiled values to strings — claim values can be any JSON type
	const s = (v: unknown): string => v == null ? '' : String(v);

	// Map compiled work entries to internal WorkGroup format
	const work: WorkGroup[] = (compiledView.workHistory || []).map(w => ({
		company: s(w.company),
		title: s(w.title),
		period: s(w.period),
		postDepartureContext: s(w.postDepartureContext),
		sectionIndex: 0, // Derived below from claims if available
		betaEnrichment: w.betaEnrichment,
		trustTier: w.trustTier,
		claims: (w.claims || []) as Claim[],
		projects: (w.projects || []).map(p => ({
			name: s(p.name),
			period: s(p.period),
			role: s(p.role),
			summary: s(p.summary),
			problemStatement: s(p.problemStatement),
			status: s(p.status),
			launchDate: s(p.launchDate),
			technicalDetails: p.technicalDetails,
			claims: (p.claims || []) as Claim[],
			extra: collectExtra(p as unknown as Record<string, unknown>, KNOWN_PROJECT_FIELDS),
		})),
		status: 'approved' as const,
		extra: collectExtra(w as unknown as Record<string, unknown>, KNOWN_WORK_FIELDS),
	}));

	// Derive sectionIndex from first claim's section path (for reorganize targeting)
	for (const group of work) {
		const firstClaim = group.claims[0] || group.projects[0]?.claims[0];
		if (firstClaim) {
			const idxMatch = firstClaim.section.match(/work_history\[(\d+)\]/);
			if (idxMatch) group.sectionIndex = parseInt(idxMatch[1]);
		}
	}

	// Compiler owns work history sort order — do not re-sort here.

	// Map compiled people to internal PersonGroup format
	const people: PersonGroup[] = (compiledView.people || []).map(p => ({
		name: s(p.name),
		title: s(p.title),
		relationship: s(p.relationship),
		companies: (Array.isArray(p.companies) ? p.companies : []).map(String).join(', '),
		projectRefs: '', // Derived from claims below
		context: s(p.context),
		recommendations: (p.recommendations || []).map(r => ({
			text: s(r.text),
			authorTitle: r.authorTitle ? String(r.authorTitle) : undefined,
			date: r.date ? String(r.date) : undefined,
			platform: r.platform ? String(r.platform) : undefined,
		})),
		claims: (p.claims || []) as Claim[],
	}));

	// Extract projectRefs from people claims
	for (const person of people) {
		const refClaim = person.claims.find(c => c.field === 'project_refs');
		if (refClaim) {
			person.projectRefs = Array.isArray(refClaim.value) ? (refClaim.value as string[]).join(', ') : String(refClaim.value || '');
		}
	}

	// Map compiled events to internal EventGroup format
	const events: EventGroup[] = (compiledView.events || []).map(e => ({
		name: s(e.name),
		type: s(e.type),
		date: s(e.date),
		significance: s(e.significance),
		personalContext: s(e.personalContext),
		projectRefs: '', // Derived from claims below
		company: s(e.company),
		claims: (e.claims || []) as Claim[],
	}));

	// Extract projectRefs from event claims
	for (const evt of events) {
		const refClaim = evt.claims.find(c => c.field === 'project_refs');
		if (refClaim) {
			evt.projectRefs = Array.isArray(refClaim.value) ? (refClaim.value as string[]).join(', ') : String(refClaim.value || '');
		}
	}

	// Sort events by date (most recent first)
	events.sort((a, b) => {
		const yearA = extractSortYear(a.date);
		const yearB = extractSortYear(b.date);
		return yearB - yearA;
	});

	// Identity and skills come as flat claim arrays — split education out of identity
	const allIdentityClaims: Claim[] = (compiledView.identity?.claims || []) as Claim[];
	const educationClaims: Claim[] = [];
	const identityClaims: Claim[] = [];
	for (const c of allIdentityClaims) {
		if (c.section.includes('education')) {
			educationClaims.push(c);
		} else {
			identityClaims.push(c);
		}
	}

	const skillClaims: Claim[] = (compiledView.skills?.claims || []) as Claim[];

	// v1 legacy project claims (not within work_history) — empty in compiled view
	const projectClaims: Claim[] = [];

	// Handle pending claims by adding them to appropriate groups
	if (pendingClaims && pendingClaims.length > 0) {
		for (const c of pendingClaims) {
			const workMatch = c.section.match(/^experience\.work_history\[(\d+)\]/);
			if (workMatch) {
				// Find matching work group by checking if any existing claim shares this session+index
				const idx = parseInt(workMatch[1]);
				const sessionId = c.sessionId || '__nosession__';
				let matched = false;
				for (const group of work) {
					const hasMatch = group.claims.some(gc =>
						gc.sessionId === sessionId &&
						gc.section.match(/work_history\[(\d+)\]/)?.[1] === String(idx)
					);
					if (hasMatch) {
						group.claims.push(c);
						group.status = 'mixed';
						matched = true;
						break;
					}
				}
				if (!matched) {
					// New work entry from pending — create temporary group
					work.push({
						company: c.field === 'company' ? str(c) : '',
						title: c.field === 'title' ? str(c) : '',
						period: c.field === 'period' ? str(c) : '',
						postDepartureContext: '',
						sectionIndex: idx,
						claims: [c],
						projects: [],
						status: 'pending',
						extra: {},
					});
				}
			} else if (c.section.includes('education')) {
				educationClaims.push(c);
			} else if (c.section.startsWith('identity')) {
				identityClaims.push(c);
			} else if (c.section.startsWith('skills')) {
				skillClaims.push(c);
			}
			// People and events pending claims are rare; skip for now
		}
	}

	return { work, skillClaims, projectClaims, identityClaims, educationClaims, people, events };
}

function formatValueForEdit(value: unknown): string {
	if (typeof value === 'string') return value;
	return JSON.stringify(value, null, 2);
}

function parseEditedValue(raw: string): unknown {
	try {
		return JSON.parse(raw);
	} catch {
		return raw;
	}
}

/** Render a single claim as a note line */
function ClaimNote({ claim, isPending, isDimmed, isRecent, editable, hideLabel, onAction, editingId, onStartEdit, onSaveEdit, onCancelEdit, editValue, onEditValueChange, resolveValue }: {
	claim: Claim;
	isPending: boolean;
	isDimmed?: boolean;
	isRecent?: boolean;
	editable?: boolean;
	hideLabel?: boolean;
	onAction: (claimId: string, action: 'approve' | 'reject', editedValue?: unknown) => void;
	editingId: string | null;
	onStartEdit: (claim: Claim) => void;
	onSaveEdit: (claimId: string) => void;
	onCancelEdit: () => void;
	editValue: string;
	onEditValueChange: (value: string) => void;
	resolveValue?: (claim: Claim) => string | null;
}) {
	// Skip metadata fields absorbed into headers
	if (['company', 'title', 'period'].includes(claim.field) &&
		claim.section.match(/^experience\.work_history\[\d+\]$/)) {
		return null;
	}

	// Skip internal/structural fields
	if (claim.field === 'beta_enrichment') return null;
	if (claim.field === 'id') return null;
	if (claim.field === 'type' && claim.section.match(/^experience\.work_history\[\d+\]$/)) return null;

	const label = claim.field.replace(/_/g, ' ');
	let content: string;

	// Check for resolved ref display
	const resolved = resolveValue?.(claim);
	if (resolved !== null && resolved !== undefined) {
		content = resolved;
	} else if (claim.narrative) {
		content = claim.narrative;
	} else if (claim.field === 'highlights') {
		const vals = Array.isArray(claim.value) ? claim.value : [claim.value];
		content = vals.map(String).join('; ');
	} else if (claim.field === 'evidence_assets') {
		const assets = Array.isArray(claim.value) ? claim.value : [claim.value];
		content = assets.map((a: any) => a?.label || a?.url || String(a)).join(', ');
	} else if (typeof claim.value === 'object' && claim.value !== null && !Array.isArray(claim.value)) {
		content = formatItem(claim.value);
	} else {
		content = formatValue(claim.value);
	}

	const isMultiline = content.includes('\n');
	const isEditing = editingId === claim.id;

	return (
		<div class={`pn-note ${isPending ? 'pn-note--pending' : 'pn-note--approved'} ${isEditing ? 'pn-note--editing' : ''} ${isDimmed ? 'pn-note--dimmed' : ''} ${isRecent ? 'pn-note--recent' : ''}`} data-claim-id={claim.id}>
			<div class="pn-note-content">
				{isEditing ? (
					<div class="pn-edit">
						<textarea
							class="pn-edit-input"
							value={editValue}
							onInput={(e) => onEditValueChange((e.target as HTMLTextAreaElement).value)}
							rows={Math.max(2, editValue.split('\n').length + 1)}
						/>
						<div class="pn-edit-actions">
							<button class="btn btn-primary btn-xs" onClick={() => onSaveEdit(claim.id)}>{isPending ? 'Save & Approve' : 'Save'}</button>
							<button class="btn btn-ghost btn-xs" onClick={onCancelEdit}>Cancel</button>
						</div>
					</div>
				) : claim.narrative ? (
					<p class="pn-narrative">{content}</p>
				) : isMultiline ? (
					<div class="pn-fact">
						{!hideLabel && <span class="pn-fact-label">{label}</span>}
						{content.split('\n').map((line, i) => <p key={i} class="pn-fact-line">{line}</p>)}
					</div>
				) : (
					<p class="pn-fact">{!hideLabel && <><span class="pn-fact-label">{label}</span>{' '}</>}{content}</p>
				)}
			</div>
			{isPending && !isEditing && (
				<div class="pn-actions">
					<button
						class="pn-action pn-action--edit"
						onClick={() => onStartEdit(claim)}
						title="Edit before approving"
					>&#9998;</button>
					<button
						class="pn-action pn-action--approve"
						onClick={() => onAction(claim.id, 'approve')}
						title="Approve"
					>&#10003;</button>
					<button
						class="pn-action pn-action--reject"
						onClick={() => onAction(claim.id, 'reject')}
						title="Reject"
					>&#10005;</button>
				</div>
			)}
			{!isPending && editable && !isEditing && (
				<div class="pn-actions">
					<button
						class="pn-action pn-action--edit"
						onClick={() => onStartEdit(claim)}
						title="Edit"
					>&#9998;</button>
					<button
						class="pn-action pn-action--reject"
						onClick={() => onAction(claim.id, 'reject')}
						title="Remove"
					>&#10005;</button>
				</div>
			)}
		</div>
	);
}

// Identity field display order — name and positioning first, personal/voice last
const IDENTITY_FIELD_ORDER = new Map<string, number>([
	['name', 0], ['preferred', 1],
	['title', 2], ['professional_identity', 3], ['tagline', 4], ['focus_areas', 5],
	['positioning', 6],
	['email', 10], ['linkedin', 11], ['portfolio', 12], ['contact', 13],
	['career_patterns', 20],
	['personal_context', 25],
	['voice', 30],
]);

export function ProfileNotes({ compiledView, pendingClaims, onClaimAction, sessionClaimIds, editable, recentClaimIds, onReorganize }: Props) {
	const [editingClaim, setEditingClaim] = useState<string | null>(null);
	const [editValue, setEditValue] = useState('');
	const [expandedPrior, setExpandedPrior] = useState<Set<string>>(new Set());

	function handleStartEdit(claim: Claim) {
		setEditingClaim(claim.id);
		setEditValue(formatValueForEdit(claim.value));
	}

	function handleSaveEdit(claimId: string) {
		const parsed = parseEditedValue(editValue);
		onClaimAction(claimId, 'approve', parsed);
		setEditingClaim(null);
		setEditValue('');
	}

	function handleCancelEdit() {
		setEditingClaim(null);
		setEditValue('');
	}

	const contentRef = useRef<HTMLDivElement>(null);

	// Auto-scroll to first recent claim when new claims arrive
	useEffect(() => {
		if (!recentClaimIds || recentClaimIds.size === 0) return;
		// Small delay to let DOM render the new claims
		const timer = setTimeout(() => {
			const el = contentRef.current?.querySelector('.pn-note--recent');
			if (el) {
				el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
			}
		}, 100);
		return () => clearTimeout(timer);
	}, [recentClaimIds]);

	const totalClaims = compiledView.stats.totalClaims + (pendingClaims?.length || 0);

	if (totalClaims === 0) {
		return (
			<div class="profile-notes">
				<div class="pn-empty">
					<p class="t-caption">Start talking to build your profile. Notes will appear here as we go.</p>
				</div>
				<style>{profileNotesStyles}</style>
			</div>
		);
	}

	const pendingIds = new Set((pendingClaims || []).map(c => c.id));
	const hasSessionScope = sessionClaimIds && sessionClaimIds.size > 0;
	let buildResult: ReturnType<typeof buildFromCompiled>;
	try {
		buildResult = buildFromCompiled(compiledView, pendingClaims);
	} catch (e) {
		console.error('[ProfileNotes] Failed to build from compiled view:', e);
		return (
			<div class="profile-notes">
				<div class="pn-empty">
					<p class="t-caption">Record display error. Please refresh the page.</p>
				</div>
				<style>{profileNotesStyles}</style>
			</div>
		);
	}
	const { work, skillClaims, projectClaims, identityClaims, educationClaims, people, events } = buildResult;
	const [peopleSortBy, setPeopleSortBy] = useState<'name' | 'company' | 'project'>('name');

	// Build slug→name lookup maps for resolving refs
	const peopleNameMap = new Map<string, string>();
	for (const p of people) {
		// Derive slug from name
		const slug = p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
		peopleNameMap.set(slug, p.name);
		// Also check if there's an explicit id claim
		const idClaim = p.claims.find(c => c.field === 'id');
		if (idClaim && typeof idClaim.value === 'string') {
			peopleNameMap.set(idClaim.value, p.name);
		}
	}
	const projectNameMap = new Map<string, string>();
	for (const w of work) {
		for (const proj of w.projects) {
			const slug = proj.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
			projectNameMap.set(slug, proj.name);
			const idClaim = proj.claims.find(c => c.field === 'id');
			if (idClaim && typeof idClaim.value === 'string') {
				projectNameMap.set(idClaim.value, proj.name);
			}
		}
	}
	const eventNameMap = new Map<string, string>();
	for (const evt of events) {
		const slug = evt.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
		eventNameMap.set(slug, evt.name);
		const idClaim = evt.claims.find(c => c.field === 'id');
		if (idClaim && typeof idClaim.value === 'string') {
			eventNameMap.set(idClaim.value, evt.name);
		}
	}

	/** Resolve an array of slug refs to display names */
	function resolveRefs(value: unknown, map: Map<string, string>): string {
		if (typeof value === 'string') {
			// Might be a comma-separated string or a single slug
			const slugs = value.split(',').map(s => s.trim());
			return slugs.map(s => map.get(s) || s).join(', ');
		}
		if (Array.isArray(value)) {
			return value.map(s => typeof s === 'string' ? (map.get(s) || s) : String(s)).join(', ');
		}
		return formatValue(value);
	}

	const noteProps = {
		editingId: editingClaim,
		onStartEdit: handleStartEdit,
		onSaveEdit: handleSaveEdit,
		onCancelEdit: handleCancelEdit,
		editValue,
		onEditValueChange: setEditValue,
		editable,
		resolveValue: (claim: Claim): string | null => {
			if (claim.field === 'team_refs') return resolveRefs(claim.value, peopleNameMap);
			if (claim.field === 'event_refs') return resolveRefs(claim.value, eventNameMap);
			if (claim.field === 'project_refs') return resolveRefs(claim.value, projectNameMap);
			return null; // use default rendering
		},
	};

	function togglePrior(key: string) {
		setExpandedPrior(prev => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}

	// Fields absorbed into parent headers or internal — skip from grouped rendering
	const SKIP_FIELDS = new Set(['company', 'title', 'period', 'id', 'beta_enrichment', 'type']);

	// Display-friendly field labels
	const FIELD_LABELS: Record<string, string> = {
		'location': 'location',
		'group': 'group',
		'professional_identity': 'professional identity',
		'team_refs': 'team',
		'event_refs': 'events',
		'project_refs': 'projects',
		'evidence_assets': 'evidence',
		'focus_areas': 'focus areas',
		'cannot_discuss': 'boundaries',
		'do_not_infer': 'do not infer',
		'quotability': 'visibility',
		'management_note': 'management context',
		'trajectory_phase': 'career phase',
	};

	function fieldLabel(field: string): string {
		return FIELD_LABELS[field] || field.replace(/_/g, ' ');
	}

	/** Group claims by field and render with shared subheaders for repeated fields.
	 *  Optional fieldOrder map controls group ordering (lower = earlier). */
	function renderGroupedClaims(claims: Claim[], isDimmed?: boolean, fieldOrder?: Map<string, number>) {
		// Filter out absorbed/internal fields and empty values
		const filtered = claims.filter(c => {
			if (SKIP_FIELDS.has(c.field)) return false;
			if (c.value === '' || c.value === null || c.value === undefined) return false;
			if (typeof c.value === 'string' && c.value.trim() === '') return false;
			return true;
		});

		// Dedup claims:
		// - Scalar fields (summary, name, role, focus, location, group, period): keep only one per section+field (latest wins)
		// - Array/multi fields (highlights, can_discuss, etc.): dedup by identical value
		// - evidence_assets: dedup by URL content
		const SCALAR_FIELDS = new Set(['summary', 'name', 'role', 'focus', 'location', 'group', 'period', 'brief', 'tagline', 'title', 'professional_identity', 'relationship', 'type', 'date', 'significance', 'trajectory_phase', 'management_note']);
		const seenScalar = new Set<string>();
		const seenValue = new Set<string>();
		const seenUrls = new Set<string>();
		const dedupFiltered: Claim[] = [];
		for (const c of filtered) {
			const sectionField = `${c.section}.${c.field}`;
			if (SCALAR_FIELDS.has(c.field)) {
				// Scalar: keep only first occurrence per section+field
				if (seenScalar.has(sectionField)) continue;
				seenScalar.add(sectionField);
			} else if (c.field === 'evidence_assets') {
				// Evidence: dedup by URL
				const urls = Array.isArray(c.value) ? c.value.map((a: any) => a?.url || String(a)).sort().join(',') : String(c.value);
				if (seenUrls.has(urls)) continue;
				seenUrls.add(urls);
			} else {
				// Multi-value: dedup by exact match, then fuzzy match for text claims
				const valStr = typeof c.value === 'string' ? c.value : JSON.stringify(c.value);
				const key = `${sectionField}:${valStr}`;
				if (seenValue.has(key)) continue;

				// Fuzzy dedup for text highlights/context: skip if >70% word overlap with existing
				if (typeof c.value === 'string' && c.value.length > 30) {
					const words = new Set(c.value.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(w => w.length > 3));
					let isFuzzyDupe = false;
					for (const existing of seenValue) {
						if (!existing.startsWith(sectionField + ':')) continue;
						const existingVal = existing.substring(sectionField.length + 1);
						const existingWords = new Set(existingVal.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(w => w.length > 3));
						if (existingWords.size === 0 || words.size === 0) continue;
						let overlap = 0;
						for (const w of words) { if (existingWords.has(w)) overlap++; }
						const similarity = overlap / Math.min(words.size, existingWords.size);
						if (similarity > 0.7) { isFuzzyDupe = true; break; }
					}
					if (isFuzzyDupe) continue;
				}

				seenValue.add(key);
			}
			dedupFiltered.push(c);
		}

		// Sort by field to group same-field claims, then group.
		const sorted = [...dedupFiltered].sort((a, b) => {
			if (fieldOrder) {
				return (fieldOrder.get(a.field) ?? 99) - (fieldOrder.get(b.field) ?? 99);
			}
			return a.field.localeCompare(b.field);
		});
		const groups: Array<{ field: string; claims: Claim[] }> = [];
		for (const c of sorted) {
			const last = groups[groups.length - 1];
			if (last && last.field === c.field) {
				last.claims.push(c);
			} else {
				groups.push({ field: c.field, claims: [c] });
			}
		}

		return groups.map((group, gi) => (
			<div key={`grp-${gi}`} class="pn-field-group">
				<p class="pn-subsection-label">{fieldLabel(group.field)}</p>
				{group.claims.map(c => (
					<ClaimNote key={c.id} claim={c} isPending={pendingIds.has(c.id)} isDimmed={isDimmed} isRecent={recentClaimIds?.has(c.id)} hideLabel onAction={onClaimAction} {...noteProps} />
				))}
			</div>
		));
	}

	/** Render a list of claims, collapsing prior-session claims when session scope is active */
	function renderClaimList(claims: (Claim & { _status?: string })[], sectionKey: string, fieldOrder?: Map<string, number>) {
		if (!hasSessionScope) {
			return renderGroupedClaims(claims, false, fieldOrder);
		}

		const sessionClaims = claims.filter(c => sessionClaimIds!.has(c.id));
		const priorClaims = claims.filter(c => !sessionClaimIds!.has(c.id));

		return (
			<>
				{renderGroupedClaims(sessionClaims, false, fieldOrder)}
				{priorClaims.length > 0 && (
					expandedPrior.has(sectionKey) ? (
						<>
							{renderGroupedClaims(priorClaims, true, fieldOrder)}
							<button class="pn-prior-toggle" onClick={() => togglePrior(sectionKey)}>
								hide {priorClaims.length} from prior sessions
							</button>
						</>
					) : (
						<button class="pn-prior-toggle" onClick={() => togglePrior(sectionKey)}>
							+ {priorClaims.length} more from your profile
						</button>
					)
				)}
			</>
		);
	}

	function renderPersonRow(person: PersonGroup, i: number) {
		const personIsRecent = recentClaimIds && person.claims.some(c => recentClaimIds.has(c.id));
		const validRecs = person.recommendations.filter(r => r.text);
		return (
			<div key={i} class={`pn-entity-row ${personIsRecent ? 'pn-entity-row--recent' : ''}`}>
				<div class="pn-entity-content">
					<span class="pn-entity-name">{person.name}{person.title && <span class="pn-person-title"> — {person.title}</span>}</span>
					<span class="pn-entity-detail">
						{person.relationship}
						{peopleSortBy !== 'company' && person.companies && `, ${person.companies}`}
						{peopleSortBy !== 'project' && person.projectRefs && <span class="pn-entity-refs"> [{resolveRefs(person.projectRefs, projectNameMap)}]</span>}
					</span>
					{person.context && <span class={`pn-entity-context ${person.context.length > 80 ? 'pn-entity-context--long' : ''}`}>{person.context}</span>}
					{validRecs.map((rec, ri) => (
						<div key={ri} class="pn-entity-recommendation">
							<span class="pn-rec-badge">{rec.platform || 'rec'}</span>
							<span class="pn-rec-text">"{rec.text}"</span>
							{(rec.authorTitle || rec.date) && (
								<span class="pn-rec-meta">
									{rec.authorTitle && ` — ${rec.authorTitle}`}
									{rec.date && `, ${rec.date}`}
								</span>
							)}
						</div>
					))}
				</div>
				{editable && (
					<div class="pn-actions">
						<button
							class="pn-action pn-action--reject"
							onClick={() => person.claims.forEach(c => onClaimAction(c.id, 'reject'))}
							title="Remove"
						>&#10005;</button>
					</div>
				)}
			</div>
		);
	}

	return (
		<div class="profile-notes">
			<div class="pn-header">
				<p class="t-label pn-title">Your Record</p>
				<p class="t-caption pn-subtitle">
					{hasSessionScope ? (
						<>
							<span class="pn-session-badge">{sessionClaimIds!.size} claims from this session</span>
							{compiledView.stats.totalClaims ? ` · ${compiledView.stats.totalClaims} claims total` : ''}
						</>
					) : (
						<>
							<span class={recentClaimIds && recentClaimIds.size > 0 ? 'pn-count-pulse' : ''} key={totalClaims}>
								{compiledView.stats.totalClaims} claims
								{pendingClaims && pendingClaims.length > 0 && ` · ${pendingClaims.length} pending`}
							</span>
						</>
					)}
					</p>
			</div>

			<div class="pn-content" ref={contentRef}>
				{/* Identity */}
				{identityClaims.length > 0 && (
					<div class="pn-section">
						<p class="pn-section-label">Identity</p>
						{renderClaimList(identityClaims, 'identity', IDENTITY_FIELD_ORDER)}
					</div>
				)}

				{/* Work History — sorted by recency, projects nested */}
				{work.length > 0 && (
					<div class="pn-section">
						<p class="pn-section-label">Timeline</p>
						<CareerTimeline workGroups={work} />
						<p class="pn-section-label" style="margin-top: 1rem;">Experiences</p>
						{work.map((group, i) => {
							const allGroupClaims = [...group.claims, ...group.projects.flatMap(p => p.claims)];
							const hasSessionClaims = !hasSessionScope || allGroupClaims.some(c => sessionClaimIds!.has(c.id));
							return (
								<div key={i} class={`pn-work-group ${!hasSessionClaims ? 'pn-section--empty-session' : ''}`}>
									<div class="pn-work-header">
										<span class="pn-work-company">
											{group.company}
											{group.trustTier && group.trustTier !== 'stated' && (
												<span class={`pn-trust-badge pn-trust-${group.trustTier}`}>{group.trustTier}</span>
											)}
											{group.betaEnrichment?.hasPublicPage && (
												<a
													href={`https://getbeta.io/company/${group.betaEnrichment.entityKey.replace(/_/g, '-')}`}
													target="_blank"
													rel="noopener noreferrer"
													class="pn-beta-link"
													title="View company intelligence on BETA"
													onClick={(e: Event) => e.stopPropagation()}
												>
													<svg class="pn-beta-logo" width="12" height="14" viewBox="0 0 820 976" fill="none" xmlns="http://www.w3.org/2000/svg">
														<circle cx="86.1176" cy="889.882" r="86.1176" fill="currentColor" opacity="0.5"/>
														<path d="M384.195 633.214C359.705 575.494 336.64 512.2 319.136 447.253C323.903 424.565 327.818 402.434 331.001 381.502C337.758 337.069 341.135 298.674 342.817 271.514C343.656 257.955 344.07 247.239 344.274 240.033C344.376 236.437 344.419 233.719 344.443 231.96C344.454 231.083 344.468 230.442 344.471 230.054V229.647C344.47 160.84 322.511 132.289 305.165 121.22C297.424 116.282 290.602 114.824 287.059 114.824C276.747 114.823 238.648 127.172 230.992 201.783C239.64 107.482 287.059 0 287.059 0C307.803 0.000184641 327.159 4.01017 344.848 11.4727C415.282 41.1864 459.293 125.637 459.294 229.647V230.81C459.29 231.458 459.282 232.366 459.266 233.516C459.235 235.815 459.174 239.1 459.056 243.285C458.819 251.658 458.341 263.659 457.416 278.607C455.567 308.47 451.885 350.327 444.521 398.757C434.097 467.306 416.017 550.998 384.195 633.214Z" fill="currentColor" opacity="0.7"/>
														<path d="M341.429 678.933C284.59 550.136 229.647 384.642 229.647 229.647C229.648 130.132 287.059 2.96204e-09 287.059 2.96204e-09C183.717 -0.000626911 114.824 99.5135 114.823 229.647C114.823 408.312 177.186 591.148 236.389 725.3C265.634 791.568 294.701 847.281 316.508 886.504C327.423 906.137 336.557 921.695 343.013 932.437C346.238 937.803 348.793 941.976 350.582 944.855C351.34 946.076 351.968 947.071 352.443 947.823L353.259 949.116L353.441 949.397L353.497 949.481L353.525 949.537L370.457 976H401.882C650.341 976 798.594 848.657 817.669 696.062C826.93 621.974 803.68 547.221 750.123 491.336C696.359 435.235 615.881 401.882 516.706 401.882V516.706C589.765 516.706 638.464 540.766 667.229 570.782C696.202 601.014 708.834 641.086 703.743 681.821C694.613 754.858 621.001 850.56 433.784 860.336C428.978 852.118 423.251 842.188 416.866 830.705C396.384 793.864 368.97 741.34 341.429 678.933Z" fill="currentColor"/>
													</svg>
												</a>
											)}
											{!hasSessionClaims && <span class="pn-section-inline-count">{allGroupClaims.length}</span>}
										</span>
										<span class="pn-work-header-actions">
											{onReorganize && group.projects.length > 0 && (
												<button
													class="pn-reorganize-btn"
													onClick={() => onReorganize(group.company, `experience.work_history[${group.sectionIndex}]`)}
													title={`Reorganize ${group.company}`}
												>reorganize</button>
											)}
											{group.period && <span class="pn-work-period">{group.period}</span>}
										</span>
									</div>
									{hasSessionClaims && (
										<>
											{group.title && <p class="pn-work-title">{group.title}</p>}
											{group.betaEnrichment && (
												<p class="pn-beta-context">
													{[
														group.betaEnrichment.sector,
														group.betaEnrichment.entityType?.replace(/_/g, ' '),
														group.betaEnrichment.scaleDescription,
														group.betaEnrichment.ticker ? `${group.betaEnrichment.ticker}` : null,
													].filter(Boolean).join(' \u00b7 ')}
												</p>
											)}
											{group.postDepartureContext && (
												<p class="pn-field-text pn-post-departure"><span class="pn-meta-label">After departure:</span> {group.postDepartureContext}</p>
											)}
											{Object.keys(group.extra).length > 0 && (
												<div class="pn-extra-fields">
													{Object.entries(group.extra).map(([k, v]) => renderGenericField(k, v))}
												</div>
											)}
											{renderClaimList(group.claims.filter(c => !['post_departure_context'].includes(c.field)), `work-${i}`)}
											{group.projects.map((proj, j) => (
												<div key={j} class="pn-project-group">
													<div class="pn-project-header">
														<span class="pn-project-name">
															{proj.name || `Project ${j + 1}`}
															{proj.status && <span class="pn-status-badge">{proj.status}</span>}
														</span>
														<span class="pn-project-header-right">
															{proj.launchDate && <span class="pn-field-inline"><span class="pn-meta-label">Launched</span> {proj.launchDate}</span>}
															{proj.period && <span class="pn-work-period">{proj.period}</span>}
														</span>
													</div>
													{proj.role && <p class="pn-project-role">{proj.role}</p>}
													{proj.problemStatement && <p class="pn-field-text">{proj.problemStatement}</p>}
													{proj.summary && <p class="pn-field-text">{proj.summary}</p>}
													{proj.technicalDetails && (
														<div class="pn-tech-details">
															{proj.technicalDetails.builtWith && proj.technicalDetails.builtWith.length > 0 && (
																<div class="pn-tag-row">
																	<span class="pn-tag-label">Built with</span>
																	{proj.technicalDetails.builtWith.map((t, ti) => <span key={ti} class="pn-tag">{t}</span>)}
																</div>
															)}
															{proj.technicalDetails.platforms && proj.technicalDetails.platforms.length > 0 && (
																<div class="pn-tag-row">
																	<span class="pn-tag-label">Platforms</span>
																	{proj.technicalDetails.platforms.map((t, ti) => <span key={ti} class="pn-tag">{t}</span>)}
																</div>
															)}
															{proj.technicalDetails.dataSources && proj.technicalDetails.dataSources.length > 0 && (
																<div class="pn-tag-row">
																	<span class="pn-tag-label">Data</span>
																	{proj.technicalDetails.dataSources.map((t, ti) => <span key={ti} class="pn-tag">{t}</span>)}
																</div>
															)}
														</div>
													)}
													{Object.keys(proj.extra).length > 0 && (
														<div class="pn-extra-fields">
															{Object.entries(proj.extra).map(([k, v]) => renderGenericField(k, v))}
														</div>
													)}
													{renderClaimList(
														proj.claims.filter(c => !['name', 'period', 'role', 'summary', 'problem_statement', 'status', 'launch_date', 'technical_details'].includes(c.field)),
														`work-${i}-proj-${j}`
													)}
												</div>
											))}
										</>
									)}
								</div>
							);
						})}
					</div>
				)}

				{/* Projects (v1 legacy) */}
				{projectClaims.length > 0 && (
					<div class="pn-section">
						<p class="pn-section-label">Projects</p>
						{renderClaimList(projectClaims, 'projects')}
					</div>
				)}

				{/* People — with sort/group controls */}
				{people.length > 0 && (
					<div class="pn-section">
						<div class="pn-section-header-row">
							<p class="pn-section-label">People<span class="pn-section-inline-count">{people.length}</span></p>
							<div class="pn-sort-controls">
								{(['name', 'company', 'project'] as const).map(mode => (
									<button
										key={mode}
										class={`pn-sort-btn ${peopleSortBy === mode ? 'pn-sort-btn--active' : ''}`}
										onClick={() => setPeopleSortBy(mode)}
									>
										{mode}
									</button>
								))}
							</div>
						</div>
						{(() => {
							// Sort/group people based on selected mode
							let sortedPeople = [...people];
							if (peopleSortBy === 'name') {
								sortedPeople.sort((a, b) => a.name.localeCompare(b.name));
							} else if (peopleSortBy === 'company') {
								sortedPeople.sort((a, b) => a.companies.localeCompare(b.companies) || a.name.localeCompare(b.name));
							} else if (peopleSortBy === 'project') {
								sortedPeople.sort((a, b) => a.projectRefs.localeCompare(b.projectRefs) || a.name.localeCompare(b.name));
							}

							// Group by the sort key for subheaders
							const groupKey = peopleSortBy === 'company' ? 'companies' : peopleSortBy === 'project' ? 'projectRefs' : null;
							if (!groupKey) {
								return sortedPeople.map((person, i) => renderPersonRow(person, i));
							}

							const groups: Array<{ label: string; people: PersonGroup[] }> = [];
							for (const p of sortedPeople) {
								const label = (groupKey === 'projectRefs' ? resolveRefs(p[groupKey], projectNameMap) : p[groupKey]) || 'Other';
								const last = groups[groups.length - 1];
								if (last && last.label === label) {
									last.people.push(p);
								} else {
									groups.push({ label, people: [p] });
								}
							}

							return groups.map((g, gi) => (
								<div key={gi} class="pn-people-group">
									<p class="pn-subsection-label">{g.label}</p>
									{g.people.map((person, i) => renderPersonRow(person, i))}
								</div>
							));
						})()}
					</div>
				)}

				{/* Events — sorted by recency */}
				{events.length > 0 && (
					<div class="pn-section">
						<p class="pn-section-label">Events</p>
						{events.map((evt, i) => {
							const evtIsRecent = recentClaimIds && evt.claims.some(c => recentClaimIds.has(c.id));
							return (
							<div key={i} class={`pn-entity-row ${evtIsRecent ? 'pn-entity-row--recent' : ''}`}>
								<div class="pn-entity-content">
									<span class="pn-entity-name">{evt.name}</span>
									<span class="pn-entity-detail">
										{evt.type}{evt.company && ` at ${evt.company}`}{evt.date && `, ${evt.date}`}
										{evt.projectRefs && <span class="pn-entity-refs"> [{resolveRefs(evt.projectRefs, projectNameMap)}]</span>}
									</span>
									{evt.significance && <span class="pn-entity-context">{evt.significance}</span>}
									{evt.personalContext && <span class="pn-entity-context pn-entity-context--personal">{evt.personalContext}</span>}
								</div>
								{editable && (
									<div class="pn-actions">
										<button
											class="pn-action pn-action--reject"
											onClick={() => evt.claims.forEach(c => onClaimAction(c.id, 'reject'))}
											title="Remove"
										>&#10005;</button>
									</div>
								)}
							</div>
							);
						})}
					</div>
				)}

				{/* Education */}
				{educationClaims.length > 0 && (
					<div class="pn-section">
						<p class="pn-section-label">Education</p>
						{renderClaimList(educationClaims, 'education')}
					</div>
				)}

				{/* Skills */}
				{skillClaims.length > 0 && (
					<div class="pn-section">
						<p class="pn-section-label">Skills</p>
						{renderClaimList(skillClaims, 'skills')}
					</div>
				)}
			</div>

			<style>{profileNotesStyles}{careerTimelineStyles}</style>
		</div>
	);
}

const profileNotesStyles = `
	.profile-notes {
		flex: 1;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	.pn-header {
		padding: 1rem;
		border-bottom: 1px solid var(--color-border);
	}

	.pn-title {
		margin-bottom: 0.25rem;
	}

	.pn-subtitle {
		color: var(--color-text-subtle);
	}

	.pn-empty {
		text-align: center;
		padding: 2rem 1rem;
	}

	.pn-content {
		flex: 1;
		overflow-y: auto;
		padding: 0.75rem 1rem;
		display: flex;
		flex-direction: column;
		gap: 0;
	}

	.pn-section {
		padding: 0.75rem 0;
		border-bottom: 1px solid var(--color-border);
	}

	.pn-section:last-child {
		border-bottom: none;
	}

	.pn-section-label {
		font-family: var(--font-mono);
		font-size: 0.625rem;
		font-weight: 500;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: var(--color-text-muted);
		margin-bottom: 0.5rem;
	}

	/* Work groups */
	.pn-work-group {
		padding: 0.5rem 0;
	}

	.pn-work-group:first-child {
		padding-top: 0;
	}

	.pn-work-header {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		gap: 0.5rem;
		flex-wrap: wrap;
	}

	.pn-work-company {
		font-size: 0.8125rem;
		font-weight: 500;
		color: var(--color-text);
	}

	.pn-work-header-actions {
		display: flex;
		align-items: baseline;
		gap: 0.5rem;
	}

	.pn-reorganize-btn {
		font-family: var(--font-mono);
		font-size: 0.5625rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-text-subtle);
		background: none;
		border: 1px solid transparent;
		border-radius: 2px;
		padding: 0.125rem 0.375rem;
		cursor: pointer;
		opacity: 0;
		transition: all 150ms;
	}

	.pn-work-group:hover .pn-reorganize-btn {
		opacity: 1;
	}

	.pn-reorganize-btn:hover {
		color: var(--color-primary);
		border-color: var(--color-primary);
	}

	.pn-work-period {
		font-family: var(--font-mono);
		font-size: 0.625rem;
		color: var(--color-text-subtle);
	}

	.pn-work-title {
		font-size: 0.75rem;
		color: var(--color-text-muted);
		margin-top: 0.125rem;
		margin-bottom: 0.25rem;
	}

	.pn-trust-badge {
		font-family: var(--font-mono);
		font-size: 0.5rem;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		padding: 0.125rem 0.375rem;
		border-radius: 2px;
		margin-left: 0.375rem;
		vertical-align: middle;
	}

	.pn-trust-structural {
		color: var(--color-text-subtle);
		background: var(--color-border);
	}

	.pn-trust-corroborated {
		color: var(--color-success, #16a34a);
		background: color-mix(in srgb, var(--color-success, #16a34a) 10%, transparent);
	}

	.pn-trust-verified {
		color: var(--color-success, #16a34a);
		background: color-mix(in srgb, var(--color-success, #16a34a) 15%, transparent);
	}

	.pn-beta-link {
		display: inline-flex;
		align-items: center;
		margin-left: 0.375rem;
		color: var(--color-text-subtle);
		text-decoration: none;
		vertical-align: middle;
		transition: color 150ms;
	}

	.pn-beta-link:hover {
		color: var(--color-primary);
	}

	.pn-beta-logo {
		display: block;
	}

	.pn-beta-context {
		font-family: var(--font-mono);
		font-size: 0.625rem;
		color: var(--color-text-subtle);
		margin-top: 0.125rem;
		margin-bottom: 0.25rem;
	}

	/* Subsections (positioning within identity, capability types within skills) */
	.pn-subsection {
		margin: 0.25rem 0 0.375rem 0;
	}

	.pn-subsection-label {
		font-family: var(--font-mono);
		font-size: 0.5625rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-text-subtle);
		margin-bottom: 0.25rem;
		padding-left: 0.625rem;
	}

	/* Field groups (shared subheader for repeated fields like highlights) */
	.pn-field-group {
		margin: 0.25rem 0;
	}

	/* Project groups (nested under work history) */
	.pn-project-group {
		margin: 0.375rem 0 0.375rem 0.75rem;
		padding-left: 0.625rem;
		border-left: 1px solid var(--color-border);
	}

	.pn-project-header {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		gap: 0.5rem;
	}

	.pn-project-name {
		font-size: 0.75rem;
		font-weight: 500;
		color: var(--color-text);
	}

	.pn-project-role {
		font-size: 0.625rem;
		color: var(--color-text-subtle);
		margin-top: 0.0625rem;
		margin-bottom: 0.125rem;
	}

	.pn-project-header-right {
		display: flex;
		align-items: baseline;
		gap: 0.5rem;
		flex-shrink: 0;
	}

	/* Compiled field metadata (summary, problem statement, post-departure, etc.) */
	.pn-field-text {
		font-size: 0.6875rem;
		color: var(--color-text-muted);
		margin: 0.125rem 0;
		line-height: 1.4;
	}

	.pn-field-inline {
		font-size: 0.625rem;
		color: var(--color-text-muted);
		display: inline-block;
		margin-right: 0.75rem;
	}

	.pn-meta-label {
		font-family: var(--font-mono);
		font-size: 0.5625rem;
		text-transform: uppercase;
		letter-spacing: 0.03em;
		color: var(--color-text-subtle);
		margin-right: 0.25rem;
	}

	.pn-post-departure {
		font-style: italic;
		opacity: 0.85;
	}

	/* Status badges */
	.pn-status-badge {
		display: inline-block;
		font-family: var(--font-mono);
		font-size: 0.5rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-text-subtle);
		background: var(--color-surface-raised);
		border: 1px solid var(--color-border);
		border-radius: 3px;
		padding: 0.0625rem 0.3125rem;
		margin-left: 0.375rem;
		vertical-align: middle;
	}

	/* Tech detail tags */
	.pn-tech-details {
		margin: 0.25rem 0;
	}

	.pn-tag-row {
		display: flex;
		align-items: baseline;
		flex-wrap: wrap;
		gap: 0.1875rem;
		margin: 0.125rem 0;
	}

	.pn-tag-label {
		font-family: var(--font-mono);
		font-size: 0.5rem;
		text-transform: uppercase;
		letter-spacing: 0.03em;
		color: var(--color-text-subtle);
		margin-right: 0.125rem;
		flex-shrink: 0;
	}

	.pn-tag {
		display: inline-block;
		font-size: 0.5625rem;
		color: var(--color-text-muted);
		background: var(--color-surface-raised);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		padding: 0 0.25rem;
		line-height: 1.5;
	}

	/* Person title */
	.pn-person-title {
		font-weight: 400;
		color: var(--color-text-muted);
		font-size: 0.6875rem;
	}

	/* Generic field fallback (future schema fields render here) */
	.pn-extra-fields {
		margin: 0.125rem 0;
	}

	.pn-field-group-meta {
		margin: 0.125rem 0;
	}

	/* Entity rows (people, events) */
	.pn-entity-row {
		display: flex;
		align-items: flex-start;
		gap: 0.375rem;
		padding: 0.25rem 0 0.25rem 0.625rem;
		border-left: 2px solid var(--color-border);
		margin: 0.125rem 0;
	}

	.pn-entity-content {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-width: 0;
	}

	.pn-entity-row:hover .pn-actions {
		opacity: 1;
	}

	.pn-section-header-row {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		margin-bottom: 0.5rem;
	}

	.pn-section-header-row .pn-section-label {
		margin-bottom: 0;
	}

	.pn-sort-controls {
		display: flex;
		gap: 0.125rem;
	}

	.pn-sort-btn {
		font-family: var(--font-mono);
		font-size: 0.5625rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-text-subtle);
		background: none;
		border: 1px solid transparent;
		border-radius: 2px;
		padding: 0.125rem 0.375rem;
		cursor: pointer;
		transition: all 150ms;
	}

	.pn-sort-btn:hover {
		color: var(--color-text-muted);
		border-color: var(--color-border);
	}

	.pn-sort-btn--active {
		color: var(--color-text);
		border-color: var(--color-border);
		background: var(--color-bg-elevated);
	}

	.pn-people-group {
		margin: 0.375rem 0;
	}

	.pn-entity-name {
		font-size: 0.75rem;
		font-weight: 500;
		color: var(--color-text);
		line-height: 1.3;
	}

	.pn-entity-detail {
		font-size: 0.625rem;
		color: var(--color-text-muted);
		line-height: 1.3;
	}

	.pn-entity-refs {
		font-family: var(--font-mono);
		font-size: 0.5625rem;
		color: var(--color-text-subtle);
	}

	.pn-entity-context {
		font-size: 0.6875rem;
		color: var(--color-text-secondary);
		margin-top: 0.25rem;
		line-height: 1.5;
		display: block;
	}

	.pn-entity-context--long {
		font-style: normal;
		padding: 0.25rem 0;
		border-top: 1px solid var(--color-border-subtle);
		margin-top: 0.375rem;
	}
	.pn-entity-context--personal {
		font-style: italic;
		opacity: 0.75;
	}
	.pn-entity-recommendation {
		display: block;
		margin-top: 0.375rem;
		padding: 0.375rem 0.5rem;
		border-left: 2px solid var(--color-border-subtle);
		font-size: 0.6875rem;
		line-height: 1.5;
	}
	.pn-rec-badge {
		display: inline-block;
		font-size: 0.5625rem;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.03em;
		background: var(--color-border-subtle);
		color: var(--color-text-secondary);
		padding: 0.0625rem 0.3125rem;
		border-radius: 0.1875rem;
		margin-right: 0.375rem;
		vertical-align: middle;
	}
	.pn-rec-text {
		color: var(--color-text-secondary);
		font-style: italic;
	}
	.pn-rec-meta {
		display: block;
		font-size: 0.625rem;
		color: var(--color-text-muted);
		margin-top: 0.1875rem;
	}

	/* Notes */
	.pn-note {
		display: flex;
		align-items: flex-start;
		gap: 0.375rem;
		padding: 0.25rem 0 0.25rem 0.625rem;
		border-left: 2px solid transparent;
		margin: 0.125rem 0;
		position: relative;
	}

	.pn-note--pending {
		border-left-color: var(--color-primary);
	}

	.pn-note--approved {
		border-left-color: var(--color-approve);
	}

	.pn-note--dimmed {
		opacity: 0.4;
		border-left-color: var(--color-border) !important;
	}

	.pn-note--recent {
		animation: pn-highlight-in 600ms var(--ease-out-expo);
		background: rgba(59, 130, 246, 0.06);
		border-radius: 2px;
	}

	@keyframes pn-highlight-in {
		from {
			opacity: 0;
			transform: translateX(-8px);
			background: rgba(59, 130, 246, 0.15);
		}
		to {
			opacity: 1;
			transform: translateX(0);
			background: rgba(59, 130, 246, 0.06);
		}
	}

	.pn-entity-row--recent {
		animation: pn-highlight-in 600ms var(--ease-out-expo);
		background: rgba(59, 130, 246, 0.06);
		border-radius: 2px;
	}

	.pn-session-badge {
		font-weight: 500;
		color: var(--color-text);
	}

	.pn-count-pulse {
		animation: pn-count-bump 600ms ease-out;
	}

	@keyframes pn-count-bump {
		0% { color: var(--color-primary); }
		100% { color: inherit; }
	}

	.pn-prior-toggle {
		font-family: var(--font-mono);
		font-size: 0.625rem;
		color: var(--color-text-subtle);
		padding: 0.25rem 0 0.25rem 0.625rem;
		cursor: pointer;
		border: none;
		background: none;
		text-align: left;
		width: 100%;
	}

	.pn-prior-toggle:hover {
		color: var(--color-text-muted);
	}

	.pn-section--empty-session {
		opacity: 0.5;
		cursor: pointer;
	}

	.pn-section--empty-session:hover {
		opacity: 0.7;
	}

	.pn-section-inline-count {
		font-family: var(--font-mono);
		font-size: 0.5625rem;
		color: var(--color-text-subtle);
		margin-left: 0.375rem;
	}

	.pn-note-content {
		flex: 1;
		min-width: 0;
		overflow-wrap: anywhere;
	}

	.pn-narrative {
		font-family: var(--font-body);
		font-size: 0.75rem;
		line-height: 1.5;
		color: var(--color-text);
		font-style: italic;
		overflow-wrap: anywhere;
	}

	.pn-fact {
		font-size: 0.75rem;
		line-height: 1.4;
		color: var(--color-text-muted);
		overflow-wrap: anywhere;
	}

	.pn-fact-label {
		font-family: var(--font-mono);
		font-size: 0.625rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-text-subtle);
		margin-right: 0.375rem;
	}

	.pn-fact-line {
		font-size: 0.75rem;
		line-height: 1.4;
		color: var(--color-text-muted);
		padding-left: 0.25rem;
		margin-top: 0.125rem;
		overflow-wrap: anywhere;
	}

	/* Actions */
	.pn-actions {
		display: flex;
		gap: 0.125rem;
		opacity: 0;
		transition: opacity 150ms;
		flex-shrink: 0;
	}

	.pn-note:hover .pn-actions,
	.pn-note--approved .pn-actions {
		opacity: 1;
	}

	.pn-note--approved .pn-actions {
		opacity: 0;
	}

	.pn-note--approved:hover .pn-actions {
		opacity: 1;
	}

	.pn-action {
		width: 1.25rem;
		height: 1.25rem;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 1px solid var(--color-border);
		border-radius: 2px;
		background: var(--color-bg);
		color: var(--color-text-muted);
		font-size: 0.625rem;
		cursor: pointer;
		transition: all 150ms;
		padding: 0;
	}

	.pn-action--approve:hover {
		background: var(--color-approve);
		border-color: var(--color-approve);
		color: white;
	}

	.pn-action--edit:hover {
		background: var(--color-primary);
		border-color: var(--color-primary);
		color: white;
	}

	.pn-action--reject:hover {
		background: var(--color-reject);
		border-color: var(--color-reject);
		color: white;
	}

	/* Editing */
	.pn-note--editing {
		border-left-color: var(--color-primary);
		background: var(--color-bg-elevated);
		padding: 0.375rem;
		border-radius: 2px;
	}

	.pn-edit {
		width: 100%;
	}

	.pn-edit-input {
		width: 100%;
		font-family: var(--font-mono);
		font-size: 0.75rem;
		line-height: 1.4;
		padding: 0.375rem;
		border: 1px solid var(--color-border);
		border-radius: 2px;
		background: var(--color-bg);
		color: var(--color-text);
		resize: vertical;
		box-sizing: border-box;
	}

	.pn-edit-input:focus {
		outline: none;
		border-color: var(--color-primary);
	}

	.pn-edit-actions {
		display: flex;
		gap: 0.25rem;
		margin-top: 0.25rem;
		justify-content: flex-end;
	}

	/* Animation for new notes */
	@keyframes pn-slide-in {
		from {
			opacity: 0;
			transform: translateY(-4px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.pn-note {
		animation: pn-slide-in 200ms ease-out;
	}
`;
